import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiWaveVoter } from "@/generated/models/ApiWaveVoter";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import Link from "next/link";
import Image from "next/image";
import { Tooltip } from "react-tooltip";
import type { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { WAVE_VOTE_STATS_LABELS, WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
interface WaveLeaderboardRightSidebarVoterProps {
  readonly voter: ApiWaveVoter;
  readonly position: number;
  readonly creditType: ApiWaveCreditType;
}
export const WaveLeaderboardRightSidebarVoter: React.FC<WaveLeaderboardRightSidebarVoterProps> = t0 => {
  const $ = _c(53);
  const {
    voter,
    position,
    creditType
  } = t0;
  const hasPositiveVotes = !!voter.positive_votes_summed;
  const hasNegativeVotes = !!voter.negative_votes_summed;
  let t1;
  if ($[0] !== position) {
    t1 = <span className="tw-text-iron-400 tw-font-medium tw-flex-shrink-0">{position}.</span>;
    $[0] = position;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `/${voter.voter.handle}`;
  let t3;
  if ($[2] !== voter.voter.handle || $[3] !== voter.voter.pfp) {
    t3 = voter.voter.pfp ? <Image src={resolveIpfsUrlSync(voter.voter.pfp)} alt={voter.voter.handle ?? "Voter"} width={20} height={20} className="tw-size-5 tw-rounded-md tw-ring-1 tw-ring-inset tw-ring-white/10 tw-bg-iron-800 tw-flex-shrink-0 tw-object-contain" /> : <div className="tw-size-5 tw-rounded-md tw-ring-1 tw-ring-inset tw-ring-white/10 tw-bg-iron-800 tw-flex-shrink-0" />;
    $[2] = voter.voter.handle;
    $[3] = voter.voter.pfp;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = voter.voter.handle ?? voter.voter.id;
  let t5;
  if ($[5] !== voter.voter.handle) {
    t5 = <span className="tw-text-sm tw-font-medium tw-text-iron-200 tw-transition-all tw-duration-300 desktop-hover:group-hover:tw-text-opacity-80 tw-truncate">{voter.voter.handle}</span>;
    $[5] = voter.voter.handle;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <UserProfileTooltipWrapper user={t4}>{t5}</UserProfileTooltipWrapper>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t6) {
    t7 = <Link href={t2} className="tw-flex tw-items-center tw-gap-2 tw-no-underline desktop-hover:hover:tw-underline tw-group desktop-hover:hover:tw-opacity-80 tw-transition-all tw-duration-300 tw-max-w-full tw-min-w-0">{t3}{t6}</Link>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t1 || $[15] !== t7) {
    t8 = <div className="tw-flex tw-items-center tw-gap-3">{t1}{t7}</div>;
    $[14] = t1;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = `voter-votes-${voter.voter.handle}-${position}`;
  let t10;
  if ($[17] !== hasPositiveVotes) {
    t10 = hasPositiveVotes && <div className="tw-w-1.5 tw-h-1.5 tw-rounded-sm tw-bg-green" />;
    $[17] = hasPositiveVotes;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== hasNegativeVotes) {
    t11 = hasNegativeVotes && <div className="tw-w-1.5 tw-h-1.5 tw-rounded-sm tw-bg-red" />;
    $[19] = hasNegativeVotes;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t9) {
    t12 = <div className="tw-flex tw-items-center tw-gap-1" data-tooltip-id={t9}>{t10}{t11}</div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = `voter-votes-${voter.voter.handle}-${position}`;
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px"
    };
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== voter.positive_votes_summed) {
    t15 = formatNumberWithCommas(voter.positive_votes_summed);
    $[26] = voter.positive_votes_summed;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15) {
    t16 = <div className="tw-text-green">+{t15}</div>;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== hasNegativeVotes || $[31] !== voter.negative_votes_summed) {
    t17 = hasNegativeVotes && <div className="tw-text-red">-{formatNumberWithCommas(voter.negative_votes_summed)}</div>;
    $[30] = hasNegativeVotes;
    $[31] = voter.negative_votes_summed;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t16 || $[34] !== t17) {
    t18 = <div className="tw-text-sm tw-space-x-1">{t16}{t17}</div>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t13 || $[37] !== t18) {
    t19 = <Tooltip id={t13} style={t14}>{t18}</Tooltip>;
    $[36] = t13;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t12 || $[40] !== t19) {
    t20 = <>{t12}{t19}</>;
    $[39] = t12;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== voter.absolute_votes_summed) {
    t21 = formatNumberWithCommas(voter.absolute_votes_summed);
    $[42] = voter.absolute_votes_summed;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const t22 = WAVE_VOTING_LABELS[creditType];
  let t23;
  if ($[44] !== t21 || $[45] !== t22) {
    t23 = <span className="tw-text-xs tw-text-iron-400 tw-whitespace-nowrap">{t21} {t22}{" "}{WAVE_VOTE_STATS_LABELS.TOTAL}</span>;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t20 || $[48] !== t23) {
    t24 = <div className="tw-flex tw-items-center tw-gap-x-3 tw-ml-6">{t20}{t23}</div>;
    $[47] = t20;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t24 || $[51] !== t8) {
    t25 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-x-2 tw-gap-y-2 tw-w-full tw-p-3 tw-rounded-lg tw-bg-iron-900 tw-overflow-hidden">{t8}{t24}</div>;
    $[50] = t24;
    $[51] = t8;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  return t25;
};